import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "react-apollo";
import {
  Modal,
  Steps,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  message,
  Radio,
  Form,
  Input,
  Upload,
  Progress,
  Switch,
  Tooltip,
  Popover,
  Spin,
  Popconfirm,
  Badge,
  Alert,
  Table,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  MinusOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  CloudUploadOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import PaginationTable from "../../../shared/PaginationTable";
import TemplateCSVDownloader from "../../../shared/TemplateCSVDownloader";
import { HashedEmailConfig, DeviceIdConfig } from "../../OrgConfig/configs/emailConfig";
import { encryptStringSHA256 } from "../../../../core/utils/encryptString";
import { useUploadProgress } from "../../../../core/components/firstPartyData/providers/UploadProgressProvider";
import GET_HASHED_EMAIL_PUBLISH_STATUS from "../../../../core/GraphQl/Queries/GET_HASHED_EMAIL_PUBLISH_STATUS";
import PublishStatusPanel from "./PublishStatusPanel";

const { Step } = Steps;
const { Title, Text } = Typography;
const { Dragger } = Upload;

// Only SHA-256 is supported. The hashing-method picker is gone, so validation
// is a fixed 64-character hex check rather than a per-algorithm length lookup.
const SHA256_HASH_LENGTH = 64;
const SHA256_HASH_PATTERN = /^[a-f0-9]{64}$/i;

// Device IDs (MAIDs/IDFAs) are uploaded verbatim as UUIDs - never hashed.
// Case-insensitive match; matched values are normalized to lowercase before
// storage since the Nexxen taxonomy CSV downstream is all-lowercase.
const DEVICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Mirrors HEM_AUDIENCE_NAME_MAX_LENGTH in external-service's nexxen-hem.naming.ts,
// which truncates the name token embedded in each SFTP partition filename. Keep the
// two in step: a lower value here is merely stricter, a higher one lets the user
// name an audience whose filename token gets silently cut short.
const AUDIENCE_NAME_MAX_LENGTH = 50;

const GuidedWorkflowSteps = ({
  visible,
  onClose,
  createHashedEmailList,
  bulkCreateHashedEmailAddress,
  activateHashedEmailList,
  refetchHashedEmailLists,
  updateHashedEmailList,
  currentOrg,
}) => {
  const [form] = Form.useForm();

  const {
    startUpload,
    updateUploadProgress,
    completeUpload,
    failUpload,
    minimizeUpload,
    uploadState,
    isMinimized,
    clearUpload,
  } = useUploadProgress();

  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 - Upload Data states
  const [emails, setEmails] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setUploadComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [savedHashedEmailCount, setHashedEmailCount] = useState(0);
  const [emailsAlreadyHashed, setEmailsAlreadyHashed] = useState(false);
  const [hemAutoDetected, setHemAutoDetected] = useState(false);
  const [detectedInvalidFormat, setDetectedInvalidFormat] = useState(null); // Track invalid data format
  const [invalidRecords, setInvalidRecords] = useState([]); // { row: number, value: string }
  const [sortOrder, setSortOrder] = useState("ascending");
  const [createdEmailListId, setCreatedEmailListId] = useState(null);

  // "emails" (Hashed Email) and "deviceIds" (Device ID) are the only selectable
  // data types. Phone Numbers and Lookalike were legacy Statara kinds and are no
  // longer offered here, but existing audiences still carry those values, so the
  // display-side label maps must keep handling them.
  const [selectedDataType, setSelectedDataType] = useState("emails");

  // CSV parsing state
  const [isParsing, setIsParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);

  // Step 2 - automatic Nexxen publish + progress states
  const [isMappingDevices, setIsMappingDevices] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);

  // Poll the publish status while on Step 2 (index 1). Skipped otherwise, or
  // before an audience id exists, or without an org to scope the query to.
  // Deliberately NOT gated on `visible`/the modal being open: this component
  // stays mounted across minimize (only the Modal's `open` prop toggles), and
  // the whole point of minimizing during a publish is that the poll — and
  // the publish itself, server-side — keeps running in the background so the
  // record is never stranded without a status update.
  // `currentOrgId` is hoisted rather than read as `currentOrg?.id` inside the
  // dependency array below: the array is evaluated during render, so a bare
  // `currentOrg.id` crashes the modal when this renders before the org context
  // resolves — and react-hooks/exhaustive-deps autofixes an optional chain in
  // a dependency array straight back to the unguarded form. A plain identifier
  // has nothing for it to strip.
  const currentOrgId = currentOrg?.id;

  // `skip` alone does NOT stop the poll. react-apollo's QueryData.execute()
  // calls updateObservableQuery() unconditionally — skip only drops the React
  // subscription and blanks `data` — and that reaches apollo-client's
  // ObservableQuery.setOptions(), which reads pollInterval directly:
  //     if (opts.pollInterval) this.startPolling(opts.pollInterval);
  //     else if (opts.pollInterval === 0) this.stopPolling();
  // So a skipped query with pollInterval 5000 still hits the network every 5s,
  // here with a null id (createdEmailListId is useState(null)), which the
  // server rejects during variable coercion:
  //   Variable "$hashedEmailListId" of non-null type "ID!" must not be null.
  // EmailTab renders this component unconditionally behind a closed modal, so
  // that fired the whole time the user sat on the First Party Data page.
  // Passing 0 is the documented stop path in the branch above.
  const shouldPollPublishStatus =
    currentStep === 1 && !!createdEmailListId && !!currentOrgId;

  const {
    data: publishStatusData,
    error: publishStatusError,
    stopPolling: stopPublishStatusPolling,
  } = useQuery(GET_HASHED_EMAIL_PUBLISH_STATUS, {
    variables: { hashedEmailListId: createdEmailListId, orgID: currentOrgId },
    skip: !shouldPollPublishStatus,
    pollInterval: shouldPollPublishStatus ? 5000 : 0,
    fetchPolicy: "network-only",
  });

  const publishStatus = publishStatusData?.getHashedEmailPublishStatus;

  // `stepProgress` is the authoritative field — the resolver keeps it
  // accurate even once Bull evicts the job and `state` degrades to
  // "unknown". Falling back to state === "completed" only covers the single
  // poll where the job just finished but stepProgress hasn't landed yet.
  const isMappingComplete =
    (publishStatus?.stepProgress ?? 0) >= 3 || publishStatus?.state === "completed";

  const isTerminalPublishState =
    (publishStatus?.stepProgress ?? 0) >= 3 || publishStatus?.state === "failed";

  // Once there's nothing left to learn, stop the 5s poll — each tick is a
  // Prisma read plus an external-service call to Bull (and a Prisma write on
  // the transition), and there's no reason to keep paying that behind a
  // closed modal or a finished audience.
  useEffect(() => {
    if (isTerminalPublishState) {
      stopPublishStatusPolling?.();
    }
  }, [isTerminalPublishState, stopPublishStatusPolling]);

  // A failed poll (network/GraphQL error, or a `success: false` payload —
  // "Audience not found", "Unauthorized: organization does not have access
  // to this audience", a missing orgID) must not leave Step 2 stuck showing
  // "Uploading…" forever while silently re-polling every 5s.
  const queryErrorMessage = publishStatusError
    ? `Couldn't check the publish status: ${publishStatusError.message || "network error"}.`
    : publishStatus && publishStatus.success === false
      ? publishStatus.error ||
        "Couldn't check the publish status. Please try reopening this audience."
      : null;

  const hasFailed =
    !!uploadErrorMessage ||
    publishStatus?.state === "failed" ||
    !!queryErrorMessage;

  // Step 2 is "in flight" from the moment it's reached until it either
  // finishes (isMappingComplete) or errors out (hasFailed) — this is the
  // window the minimize control should be available in, mirroring how it's
  // available for the whole of Step 1's upload.
  const isPublishInFlight = currentStep === 1 && !isMappingComplete && !hasFailed;

  // Mirror Step 2's publish status into the shared upload-progress context.
  // MinimizedUploadCard has no GraphQL access of its own — it only reads
  // `uploadState` — so this is the one place that translates the raw poll
  // (which can carry Bull's internal state names, including "unknown" for an
  // evicted-but-still-fine job) into the small, UI-safe vocabulary the card
  // renders: "publishing" | "completed" | "failed". `hasFailed` already
  // excludes "unknown" (see above), so an evicted job never shows as failed.
  useEffect(() => {
    if (currentStep !== 1 || !createdEmailListId) {
      return;
    }

    updateUploadProgress({
      phase: "publish",
      currentStep: 1,
      status: hasFailed ? "failed" : isMappingComplete ? "completed" : "publishing",
      progress: publishStatus?.progress ?? 0,
      errorMessage: hasFailed
        ? uploadErrorMessage ||
          publishStatus?.failedReason ||
          queryErrorMessage ||
          "Publish failed. Please try again."
        : undefined,
    });
  }, [
    currentStep,
    createdEmailListId,
    publishStatus,
    hasFailed,
    isMappingComplete,
    uploadErrorMessage,
    queryErrorMessage,
    updateUploadProgress,
  ]);

  // Rehydrate local wizard state from the shared context whenever this
  // component (re)mounts with a persisted upload/publish in flight — covers
  // both "the CSV upload finished while this modal was closed" and
  // "restoring into an in-flight Nexxen publish after this component
  // remounted" (e.g. the user minimized, navigated to another page, and came
  // back via the minimized card's restore button). Guarded on
  // createdEmailListId so it only runs once per audience and never clobbers
  // this instance's own state while it's the one actively driving the work.
  useEffect(() => {
    if (!uploadState || !uploadState.emailListId || createdEmailListId) {
      return;
    }

    form.setFieldsValue({ name: uploadState.audienceName });
    setHashedEmailCount(uploadState.savedRecords || 0);
    setCreatedEmailListId(uploadState.emailListId);
    setCurrentStep(uploadState.currentStep || 0);

    if (uploadState.phase === "publish") {
      setUploadComplete(true);
    } else if (uploadState.status === "completed") {
      setUploadComplete(true);
      setProgress(100);
      if (visible) {
        message.success("Upload completed while you were away!");
      }
    } else if (uploadState.status === "uploading") {
      // The CSV upload loop is a plain async function driven by the
      // provider's context, not by this component's lifecycle, so it keeps
      // running regardless of whether this instance is mounted — but a
      // fresh mount still needs its own `isUploading` flag set so the Step 1
      // UI (spinner, disabled inputs, the minimize control) reflects that
      // truthfully instead of showing the idle "drop a CSV" state.
      setIsUploading(true);
      setProgress(uploadState.progress || 0);
    }
  }, [uploadState, createdEmailListId, form, visible]);

  // "Still running", as opposed to "was minimized at some point". Closing or
  // reopening must never wipe work that is genuinely mid-flight, but anything
  // terminal (completed / failed / nothing started) is safe to clear.
  const isWorkInFlight =
    !!uploadState &&
    (uploadState.status === "uploading" || uploadState.status === "publishing");

  const resetWizardState = useCallback(() => {
    // Clearing createdEmailListId below flips the publish-status query's
    // `skip` to true, but react-apollo 3.1 does not tear down an already
    // running `pollInterval` when `skip` becomes true — the timer keeps
    // firing with the now-null id, and the server rejects the whole
    // operation before any resolver runs:
    //   Variable "$hashedEmailListId" of non-null type "ID!" must not be null.
    // (An explicit null, not a missing key: HttpLink JSON-stringifies the
    // variables, so `undefined` would have been dropped instead.)
    // stopPolling is otherwise only reached from the isTerminalPublishState
    // effect above, which never runs for a publish abandoned mid-flight —
    // so the reset has to stop the poll itself, while the id is still valid.
    stopPublishStatusPolling?.();
    setCurrentStep(0);
    form.resetFields();
    setEmails([]);
    setProgress(0);
    setHashedEmailCount(0);
    setUploadComplete(false);
    setIsMappingDevices(false);
    setUploadErrorMessage(null);
    setEmailsAlreadyHashed(false);
    setHemAutoDetected(false);
    setDetectedInvalidFormat(null);
    setInvalidRecords([]);
    setCreatedEmailListId(null);
    setIsUploading(false);
    clearUpload();
  }, [form, clearUpload, stopPublishStatusPolling]);

  // Start every new audience from a clean wizard. This component stays mounted
  // between openings, so without this the previous audience's name, counts and
  // step are still in state — and the restore-from-minimized effect below would
  // helpfully put the finished audience back, making it impossible to create a
  // second audience without reloading the page.
  // Seeded with the mount-time value so this fires only on a real closed->open
  // transition. Treating mount as an "open" would reset a wizard that mounted
  // already-visible — which is how a restore looks after a page reload, and
  // would throw away the very state it is meant to bring back.
  const wasVisibleRef = useRef(visible);
  useEffect(() => {
    const justOpened = visible && !wasVisibleRef.current;
    wasVisibleRef.current = visible;

    // In-flight work is the one thing that must survive a reopen: that is the
    // restore path, where the user minimized mid-publish and came back.
    if (justOpened && !isWorkInFlight) {
      resetWizardState();
    }
  }, [visible, isWorkInFlight, resetWizardState]);

  const emailRegex = /\S+@\S+\.\S+/;

  const getDataTypeLabel = () =>
    selectedDataType === "deviceIds" ? "Device IDs" : "Emails";

  // The sample template has to follow the selected type. Handing a user the
  // email sample under a "Device IDs" button yields a file where every row
  // fails validation -- the button's label already switches, so the contents
  // must too.
  const activeCsvTemplate =
    selectedDataType === "deviceIds"
      ? DeviceIdConfig.csvTemplate
      : HashedEmailConfig.csvTemplate;

  const steps = [
    {
      title: "Upload Hashed Emails",
      icon: <UploadOutlined />,
      description:
        "Your SHA-256 hashed email list has been uploaded and validated",
    },
    {
      title: "Create Taxonomy & Upload",
      icon: <CloudUploadOutlined />,
      description:
        "Creating the Nexxen taxonomy entry and uploading your audience file to the Nexxen SFTP",
    },
    {
      title: "Waiting for Activation",
      icon: <ClockCircleOutlined />,
      description:
        "Nexxen is ingesting your audience. This typically completes within 24 hours",
    },
    {
      title: "Activated",
      icon: <CheckCircleOutlined />,
      description:
        "Your audience is live in Nexxen and refreshes automatically every 60 days",
    },
  ];

  // Function to clean HEM markers from data
  const cleanHEMMarkers = (dataArray) => {
    // Filter out common HEM column headers and markers
    const hemMarkers = [
      'HEM',
      'HEM (Invalid Value)',
      'Hashed Email',
      'Hashed Emails',
      'Email Hash',
      'SHA256',
      'Hash'
    ];

    return dataArray.filter(item => {
      const trimmedItem = item.trim();
      // Filter out exact matches (case insensitive) and empty values
      return trimmedItem && !hemMarkers.some(marker =>
        marker.toLowerCase() === trimmedItem.toLowerCase()
      );
    });
  };

  // Function to detect if data is already hashed and determine the hash type
  // Also detects invalid/unsupported data formats
  const detectHashedEmails = (dataArray) => {
    if (dataArray.length === 0) return { isHashed: false, detectedMethod: null, isInvalid: false, invalidFormat: null };

    const hexRegex = /^[0-9a-fA-F]+$/;
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;

    let hashCounts = { MD5: 0, SHA1: 0, SHA256: 0, SHA512: 0 };
    let originalFormatCount = 0;
    let invalidCounts = { base64: 0, base64url: 0, other: 0 };

    dataArray.forEach((item) => {
      const trimmedItem = item.trim();
      if (trimmedItem) {
        const len = trimmedItem.length;
        const isHex = hexRegex.test(trimmedItem);

        // Check for valid hash formats
        if (isHex) {
          if (len === 32) hashCounts.MD5++;
          else if (len === 40) hashCounts.SHA1++;
          else if (len === 64) hashCounts.SHA256++;
          else if (len === 128) hashCounts.SHA512++;
          else invalidCounts.other++;
        }
        // Check for original format (email)
        else {
          const isOriginalFormat = emailRegex.test(trimmedItem);
          if (isOriginalFormat) {
            originalFormatCount++;
          }
          // Check for Base64 URL-safe format (commonly used for hashed data)
          else if (base64UrlRegex.test(trimmedItem) && len >= 20 && len <= 50) {
            invalidCounts.base64url++;
          }
          // Check for standard Base64
          else if (base64Regex.test(trimmedItem) && len >= 20) {
            invalidCounts.base64++;
          }
          else {
            invalidCounts.other++;
          }
        }
      }
    });

    // Find the most common hash type
    const totalHashes = hashCounts.MD5 + hashCounts.SHA1 + hashCounts.SHA256 + hashCounts.SHA512;
    const totalInvalid = invalidCounts.base64 + invalidCounts.base64url + invalidCounts.other;
    const totalEntries = dataArray.length;

    // Check if most data is valid hashes
    if (totalHashes > 0) {
      const hashPercentage = totalHashes / totalEntries;
      if (hashPercentage > 0.7) {
        // Determine which hash method was detected
        const sorted = Object.entries(hashCounts).sort((a, b) => b[1] - a[1]);
        const detectedMethod = sorted[0][1] > 0 ? sorted[0][0] : "SHA256";
        return { isHashed: true, detectedMethod, isInvalid: false, invalidFormat: null };
      }
    }

    // Check if most data is invalid/unsupported format
    if (totalInvalid > 0) {
      const invalidPercentage = totalInvalid / totalEntries;
      if (invalidPercentage > 0.5) {
        // Determine which invalid format was detected
        let invalidFormat = null;
        if (invalidCounts.base64url > invalidCounts.base64 && invalidCounts.base64url > invalidCounts.other) {
          invalidFormat = "Base64 URL-safe encoded";
        } else if (invalidCounts.base64 > invalidCounts.other) {
          invalidFormat = "Base64 encoded";
        } else if (invalidCounts.other > 0) {
          invalidFormat = "unsupported";
        }
        return { isHashed: false, detectedMethod: null, isInvalid: true, invalidFormat };
      }
    }

    return { isHashed: false, detectedMethod: null, isInvalid: false, invalidFormat: null };
  };

  const sortIcon = sortOrder === "ascending" ? <SortAscendingOutlined /> : <SortDescendingOutlined />;

  // Email validation and processing functions
  const removeDuplicates = arr => Array.from(new Set(arr));

  const convertToLowercase = arr => arr.map(e => e.toLowerCase());

  const containsEmail = arr => arr.some(element => emailRegex.test(element));

  const findInvalidEmail = arr => arr.find(element => !emailRegex.test(element)) || null;

  // Check if array contains non-hashed values (accepts MD5, SHA1, SHA256, SHA512)
  function hasNonHashedValue(arr) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    return !arr.every(str => {
      const isValidLength = str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128;
      return (
        isValidLength &&
        hexRegex.test(str) &&
        str !== str.split("").reverse().join("")
      );
    });
  }

  // Validate if a single string is a valid hash format (accepts MD5, SHA1, SHA256, SHA512)
  function isValidHashedValue(str) {
    const hexRegex = /^[0-9a-fA-F]+$/;
    const isValidLength = str.length === 32 || str.length === 40 || str.length === 64 || str.length === 128;
    const isHexFormat = hexRegex.test(str);
    const isNotPalindrome = str !== str.split("").reverse().join("");
    return isValidLength && isHexFormat && isNotPalindrome;
  }

  // Download invalid records as CSV
  const downloadInvalidRecordsCsv = () => {
    const csvContent = Papa.unparse({
      fields: ["Row", "Value"],
      data: invalidRecords.map(r => [r.row, r.value])
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "invalid_records.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Only SHA-256 hashes are accepted.
  const getHashLength = () => SHA256_HASH_LENGTH;

  // Function to detect the format of uploaded data
  const detectDataFormat = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return null;

    const hexRegex = /^[0-9a-fA-F]+$/;
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;

    let formatCounts = {
      md5: 0,
      sha1: 0,
      sha256: 0,
      sha512: 0,
      base64: 0,
      base64url: 0,
      unknown: 0
    };

    const sampleSize = Math.min(dataArray.length, 100);
    const sample = dataArray.slice(0, sampleSize);

    sample.forEach((item) => {
      const trimmedItem = item.trim();
      if (!trimmedItem) return;

      const len = trimmedItem.length;
      const isHex = hexRegex.test(trimmedItem);

      // Check for hex-based hashes first
      if (isHex) {
        if (len === 32) formatCounts.md5++;
        else if (len === 40) formatCounts.sha1++;
        else if (len === 64) formatCounts.sha256++;
        else if (len === 128) formatCounts.sha512++;
        else formatCounts.unknown++;
      }
      // Check for Base64 URL-safe format (commonly used for hashed data)
      else if (base64UrlRegex.test(trimmedItem) && len >= 20 && len <= 50) {
        formatCounts.base64url++;
      }
      // Check for standard Base64
      else if (base64Regex.test(trimmedItem) && len >= 20) {
        formatCounts.base64++;
      }
      else {
        formatCounts.unknown++;
      }
    });

    // Find the most common format
    const total = sampleSize;
    const entries = Object.entries(formatCounts).filter(([key]) => key !== 'unknown');
    const sorted = entries.sort((a, b) => b[1] - a[1]);

    if (sorted.length > 0 && sorted[0][1] > 0) {
      const [format, count] = sorted[0];
      const percentage = (count / total) * 100;

      if (percentage >= 50) {
        const formatNames = {
          md5: 'MD5 hash',
          sha1: 'SHA-1 hash',
          sha256: 'SHA-256 hash',
          sha512: 'SHA-512 hash',
          base64: 'Base64 encoded',
          base64url: 'Base64 URL-safe encoded'
        };
        return formatNames[format] || format;
      }
    }

    return null;
  };

  // Function to validate that all data matches the required SHA-256 format
  const validateHashFormat = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return { isValid: true, detectedFormat: null };

    const hexRegex = /^[0-9a-fA-F]+$/;
    let invalidCount = 0;
    let detectedFormat = null;

    // Check a sample for the detected format
    const sampleSize = Math.min(dataArray.length, 100);
    const sample = dataArray.slice(0, sampleSize);

    let formatCounts = { MD5: 0, SHA1: 0, SHA256: 0, SHA512: 0, other: 0 };

    sample.forEach((item) => {
      const trimmedItem = item.trim();
      if (!trimmedItem) return;

      const len = trimmedItem.length;
      const isValid = SHA256_HASH_PATTERN.test(trimmedItem);

      if (isValid) {
        return; // Valid SHA-256 hash
      }

      invalidCount++;

      // Track what format was detected instead
      const isHex = hexRegex.test(trimmedItem);
      if (isHex) {
        if (len === 32) formatCounts.MD5++;
        else if (len === 40) formatCounts.SHA1++;
        else if (len === 64) formatCounts.SHA256++;
        else if (len === 128) formatCounts.SHA512++;
        else formatCounts.other++;
      } else {
        formatCounts.other++;
      }
    });

    if (invalidCount > 0) {
      // Determine the most common detected format
      const sorted = Object.entries(formatCounts).sort((a, b) => b[1] - a[1]);
      if (sorted[0][1] > 0 && sorted[0][0] !== 'other') {
        const formatNames = {
          MD5: 'MD5 (32 characters)',
          SHA1: 'SHA-1 (40 characters)',
          SHA256: 'SHA-256 (64 characters)',
          SHA512: 'SHA-512 (128 characters)',
        };
        detectedFormat = formatNames[sorted[0][0]];
      }
      return { isValid: false, detectedFormat };
    }

    return { isValid: true, detectedFormat: null };
  };

  // Progress tracking function
  const updateProgress = async (stepProgress, status, dsp = null) => {
    if (!createdEmailListId) return;
    
    try {
      await updateHashedEmailList({
        id: createdEmailListId,
        stepProgress: stepProgress,
        status: status,
        ...(dsp && { dsp: dsp.toUpperCase() })
      });
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const customRequest = ({ file, onError }) => {
    if (file.name.toLowerCase().includes("csv")) {
      // Reset upload complete state when starting a new CSV upload
      if (isUploadComplete) {
        setUploadComplete(false);
        setHashedEmailCount(0);
        setProgress(0);
      }

      // Set parsing state to show UI feedback
      setIsParsing(true);
      setParsingProgress(0);

      // Variables to track parsing progress
      const allRows = [];
      const fileSize = file.size;
      let lastProgress = 0;

      Papa.parse(file, {
        skipEmptyLines: "greedy",
        // Note: Web Worker mode causes issues with data access in some environments
        // Using chunked parsing instead for performance without worker thread
        chunkSize: 1024 * 1024 * 10, // Process in 10MB chunks to prevent browser hang

        // Chunk callback fires for each chunk parsed - use for accurate progress tracking
        chunk: function(results, parser) {
          // Store rows from this chunk - use concat to avoid stack overflow with large arrays
          if (results.data && results.data.length > 0) {
            // Use Array.prototype.push.apply with slice for large arrays to avoid stack overflow
            for (let i = 0; i < results.data.length; i++) {
              allRows.push(results.data[i]);
            }
          }

          // Calculate progress based on file stream position
          // parser.streamer gives us access to the current byte position
          if (parser.streamer && parser.streamer._input) {
            // Calculate how much of the file has been read
            const streamedLength = parser.streamer._input.length;
            const progress = Math.min(90, Math.floor((streamedLength / fileSize) * 100));

            // Only update if progress has increased by at least 1%
            if (progress > lastProgress) {
              lastProgress = progress;
              setParsingProgress(progress);
            }
          }
        },

        error: function (err, file, inputElem, reason) {
          setIsParsing(false);
          setParsingProgress(0);
          message.error("Error parsing CSV file");
          onError({ err, file, inputElem, reason });
        },

        complete: async () => {
          // Set progress to 90% as we start processing the data
          setParsingProgress(90);

          // Use setTimeout to defer processing and prevent UI blocking
          setTimeout(async () => {
            try {
              setParsingProgress(93);

              // Track original row numbers before flattening
              const valueToRow = new Map();
              const flatValues = [];
              allRows.forEach((row, rowIndex) => {
                if (Array.isArray(row)) {
                  row.forEach(cell => {
                    const val = typeof cell === 'string' ? cell.trim() : String(cell).trim();
                    if (val) {
                      flatValues.push(val);
                      const key = val.toLowerCase();
                      if (!valueToRow.has(key)) {
                        valueToRow.set(key, rowIndex + 1);
                      }
                    }
                  });
                }
              });

              let currentEmails = removeDuplicates(flatValues);

              const isDeviceIdType = selectedDataType === "deviceIds";

              setParsingProgress(95);
              // Clean HEM markers from the data - only meaningful for the
              // hashed-email auto-detect flow below, not for Device IDs.
              const cleanedEmails = isDeviceIdType ? currentEmails : cleanHEMMarkers(currentEmails);
              const wasHEMCleaned = !isDeviceIdType && cleanedEmails.length < currentEmails.length;

              // Use cleaned data
              currentEmails = cleanedEmails;

              setParsingProgress(97);
              // Auto-detect if data is hashed before processing. Device IDs
              // are never hashed, so this detection doesn't apply to them.
              const hashDetection = isDeviceIdType
                ? { isHashed: false, detectedMethod: null, isInvalid: false, invalidFormat: null }
                : detectHashedEmails(currentEmails);
              const areHashedEmails = hashDetection.isHashed;

              // Only convert to lowercase for emails if not hashed. Device
              // IDs are always normalized to lowercase, regardless of the
              // case they were uploaded in, since the Nexxen taxonomy CSV
              // they're matched against downstream is all-lowercase.
              let processedEmails = currentEmails;

              if (isDeviceIdType || !areHashedEmails) {
                processedEmails = convertToLowercase(currentEmails);
              }

              setParsingProgress(99);
              const combinedEmails = [...emails, ...processedEmails];
              const uniqueEmails = removeDuplicates(combinedEmails);

              // Split valid and invalid records
              const validRecords = [];
              const newInvalidRecords = [];

              uniqueEmails.forEach(record => {
                const trimmed = record.trim();
                let isValid = true;

                if (isDeviceIdType) {
                  isValid = DEVICE_ID_PATTERN.test(trimmed);
                } else if (hashDetection.isInvalid) {
                  isValid = false;
                } else if (areHashedEmails || emailsAlreadyHashed) {
                  isValid = isValidHashedValue(trimmed);
                } else {
                  isValid = emailRegex.test(trimmed);
                }

                if (isValid) {
                  validRecords.push(record);
                } else {
                  const rowNum = valueToRow.get(record.toLowerCase()) || valueToRow.get(record) || 0;
                  newInvalidRecords.push({ row: rowNum, value: record });
                }
              });

              setEmails(validRecords);

              // Accumulate invalid records (dedup by value)
              if (newInvalidRecords.length > 0) {
                setInvalidRecords(prev => {
                  const combined = [...prev, ...newInvalidRecords];
                  const seen = new Set();
                  return combined.filter(r => {
                    if (seen.has(r.value)) return false;
                    seen.add(r.value);
                    return true;
                  });
                });
              }

              // Set to 100% before showing success
              setParsingProgress(100);

              // Check for invalid/unsupported data format
              if (hashDetection.isInvalid) {
                setDetectedInvalidFormat(hashDetection.invalidFormat || "unsupported");
                const dataLabel = "email addresses";
                const formatMsg = hashDetection.invalidFormat
                  ? `Your data appears to be ${hashDetection.invalidFormat}.`
                  : "Your data is in an unsupported format.";
                message.warning({
                  content: `${formatMsg} Only plain ${dataLabel} or hashed data (MD5, SHA-1, SHA-256, SHA-512) are supported.`,
                  duration: 5
                });
              }
              // Auto-detect if data is hashed and toggle the switch
              else if (areHashedEmails && !emailsAlreadyHashed) {
                setDetectedInvalidFormat(null);
                setEmailsAlreadyHashed(true);
                setHemAutoDetected(true);
                // Track the detected hash method for messaging only — validation is always SHA-256
                const detectedMethod = hashDetection.detectedMethod || "SHA256";

                // Show appropriate message based on whether HEM markers were cleaned
                if (wasHEMCleaned) {
                  message.success({ content: "Detected HEM. Cleaned and set hashing to SHA256.", duration: 3 });
                } else {
                  const dataLabel = "emails";
                  if (newInvalidRecords.length > 0) {
                    message.warning({
                      content: `Detected ${detectedMethod} hashed ${dataLabel}. ${validRecords.length.toLocaleString()} valid records loaded, ${newInvalidRecords.length.toLocaleString()} invalid records found.`,
                      duration: 5
                    });
                  } else {
                    message.info({ content: `Detected ${detectedMethod} hashed ${dataLabel} - automatically enabled pre-hashed data mode`, duration: 3 });
                  }
                }
              } else {
                setDetectedInvalidFormat(null);
                if (newInvalidRecords.length > 0) {
                  message.warning({
                    content: `Loaded ${validRecords.length.toLocaleString()} valid records. ${newInvalidRecords.length.toLocaleString()} invalid records found.`,
                    duration: 5
                  });
                } else {
                  message.success({ content: `Successfully loaded ${validRecords.length.toLocaleString()} records`, duration: 3 });
                }
              }

              // Small delay before hiding the progress indicator to show 100%
              setTimeout(() => {
                setIsParsing(false);
                setParsingProgress(0);
              }, 500);
            } catch (error) {
              message.error({ content: "Error processing CSV data", duration: 3 });
              setIsParsing(false);
              setParsingProgress(0);
            }
          }, 100);
        },
      });
    } else {
      message.error("Invalid file type. Only accepts .csv");
      return;
    }
  };

  const isNameEmpty = () => {
    const nameValue = form.getFieldValue("name");
    return !nameValue || nameValue.trim() === "";
  };

  const isNameValid = () => {
    const nameValue = form.getFieldValue("name");
    if (!nameValue || nameValue.trim() === "") return false;
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    return validNameRegex.test(nameValue.trim());
  };

  const handleSortByName = () => {
    let sorted;
    if (sortOrder === "ascending") {
      sorted = [...emails].sort((a, b) => a.localeCompare(b));
      setSortOrder("descending");
    } else {
      sorted = [...emails].sort((a, b) => b.localeCompare(a));
      setSortOrder("ascending");
    }
    setEmails(sorted);
  };

  // Step 1: Upload Emails
  const handleUploadEmails = async () => {
    let encryptedEmails = [];

    if (isNameEmpty()) {
      return message.error("Please input list name.");
    }

    if (!isNameValid()) {
      return message.error("Name can only contain letters, numbers, dash (-) and underscore (_). No spaces or other special characters allowed.");
    }

    if (selectedDataType === "emails" && emailsAlreadyHashed) {
      // Validate that data matches the required SHA-256 format
      const hashValidation = validateHashFormat(emails);
      if (!hashValidation.isValid) {
        const expectedLength = getHashLength();
        const formatMsg = hashValidation.detectedFormat
          ? `Your data appears to be ${hashValidation.detectedFormat}.`
          : "Your data is not in a valid SHA-256 format.";
        return message.warning(
          `${formatMsg} Expected SHA-256 hashed data (${expectedLength} hexadecimal characters). Please re-hash your data and upload again.`
        );
      }

      if (containsEmail(emails)) {
        return message.warning(
          "One or more of the values in your data contains an email address. Please ensure that all values are hashed or use an email-free dataset."
        );
      }
    }

    // Confirm exclusion of invalid records before uploading
    if (invalidRecords.length > 0) {
      const proceed = await new Promise((resolve) => {
        Modal.confirm({
          title: "Invalid Records Detected",
          icon: <ExclamationCircleOutlined />,
          content: `${invalidRecords.length.toLocaleString()} invalid records will be excluded from the upload. Continue with ${emails.length.toLocaleString()} valid records?`,
          okText: "Continue Upload",
          cancelText: "Cancel",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!proceed) return;
    }

    // Initialize upload progress state (after validation passes)
    startUpload({
      audienceName: form.getFieldValue("name"),
      dataType: selectedDataType,
      totalRecords: emails.length,
      savedRecords: 0,
      currentStep: 0,
      emailListId: null,
    });

    // Set uploading state immediately to show loading indicator
    setIsUploading(true);

    // Defer hashing operation to next tick to allow UI to update first
    await new Promise(resolve => setTimeout(resolve, 0));

    if (selectedDataType === "deviceIds") {
      // Device IDs are uploaded verbatim (already lowercase-normalized
      // during parsing) - never SHA-256 hashed.
      encryptedEmails = emails;
    } else if (!emailsAlreadyHashed) {
      // For large datasets, process hashing in chunks to prevent UI blocking
      if (emails.length > 5000) {
        encryptedEmails = [];
        const hashChunkSize = 5000;
        for (let i = 0; i < emails.length; i += hashChunkSize) {
          const chunk = emails.slice(i, i + hashChunkSize);
          const hashedChunk = chunk.map(email =>
            encryptStringSHA256(email)
          );
          encryptedEmails.push(...hashedChunk);

          // Yield to browser between chunks to keep UI responsive
          if (i + hashChunkSize < emails.length) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      } else {
        // For smaller datasets, hash all at once
        encryptedEmails = emails.map(email =>
          encryptStringSHA256(email)
        );
      }
      setEmails(encryptedEmails);
    } else {
      encryptedEmails = emails;
    }

    const chunkSize = 3000;

    let progressCount = 0;
    let isLastIteration = false;

    try {
      const response = await createHashedEmailList({
        name: form.getFieldValue("name"),
        stepProgress: 1,
        status: "UPLOADED",
        typeOfFirstPartyData: selectedDataType
      });

      const emailListId = response.data.createHashedEmailList.id;
      setCreatedEmailListId(emailListId);

      updateUploadProgress({
        emailListId: emailListId,
      });

      for (let i = 0; i < encryptedEmails.length; i += chunkSize) {
        const chunk = encryptedEmails.slice(i, i + chunkSize);
        progressCount += chunkSize;

        isLastIteration = i + chunkSize >= encryptedEmails.length;
        const response = await bulkCreateHashedEmailAddress({
          emailListId: emailListId,
          emails: chunk,
          refetch: isLastIteration,
        });

        const uniqueEmailsCount = response.data.bulkCreateHashedEmailAddress.uniqueEmails;

        setHashedEmailCount(
          prevCount => prevCount + uniqueEmailsCount
        );

        const currentProgress = Math.floor((progressCount / encryptedEmails.length) * 100);
        setProgress(currentProgress);

        updateUploadProgress({
          progress: currentProgress,
          savedRecords: progressCount > encryptedEmails.length ? encryptedEmails.length : progressCount,
        });
      }

      await updateHashedEmailList({
        id: emailListId,
        tag: String(encryptedEmails.length)
      });

      setIsUploading(false);
      setUploadComplete(true);

      completeUpload({
        savedRecords: savedHashedEmailCount + encryptedEmails.length,
        currentStep: 0,
      });

      message.success("Email upload completed successfully!");

    } catch (error) {
      setIsUploading(false);

      failUpload(error.message || "Error uploading emails");

      message.error("Error uploading emails: " + error.message);
    }
  };

  // Step 2 runs itself: Nexxen is the only destination, so there is nothing
  // for the user to choose. Guarded on createdEmailListId so it fires exactly
  // once per audience, and on isMappingDevices so a re-render cannot
  // double-submit.
  //
  // It also waits on the polled `publishStatus` before firing at all: the
  // restore effect can repopulate createdEmailListId with an audience that
  // was already published in a previous session (stepProgress 2+). Firing
  // blind against that id makes the server correctly refuse it, which then
  // paints a healthy in-flight audience as a red failure. Requiring a loaded
  // publishStatus with stepProgress < 2 costs one network round trip on the
  // normal path but closes that race for good.
  //
  // Note: the `activateHashedEmailList` prop is the controller-level wrapper
  // (see FirstPartyDataMainController), which expects a flat data object and
  // wraps it into the Apollo `variables` payload itself.
  //

  useEffect(() => {
    if (
      currentStep !== 1 ||
      !createdEmailListId ||
      isMappingDevices ||
      !publishStatus ||
      publishStatus.stepProgress >= 2
    ) {
      return;
    }

    setIsMappingDevices(true);
    setUploadErrorMessage(null);

    activateHashedEmailList({
      orgID: currentOrgId,
      hashedEmailListIds: [createdEmailListId],
      dsp: "NEXXEN",
    })
      .then((response) => {
        const result = response?.data?.activateHashedEmailList;
        if (result && result.success === false) {
          setUploadErrorMessage(
            result.errors?.join("; ") ||
              result.message ||
              result.error ||
              "Failed to start the Nexxen upload. Please try again.",
          );
        }
      })
      .catch((error) => {
        // isMappingDevices is deliberately left true here: it is the guard that
        // stops this effect auto-firing again, and an automatic retry against a
        // publish that may already be half-started is worse than none. The user
        // retries from the audience detail page instead.
        setUploadErrorMessage(
          error.message || "Failed to start the Nexxen upload. Please try again.",
        );
      });
  }, [currentStep, createdEmailListId, isMappingDevices, publishStatus, activateHashedEmailList, currentOrgId]);


  // Step 2 no longer has a "Next" button — it fires and progresses itself
  // (see the auto-fire effect above), so this only drives Step 1's upload.
  const handleNext = () => {
    if (currentStep === 0 && !isUploadComplete) {
      handleUploadEmails();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFinishAndCheckLater = () => {
    message.success("Workflow completed! You can check the status later from the main view.");
    handleClose();
  };

  const handleClose = async () => {
    // First call onClose to close the modal immediately
    onClose();

    // Then reset all states after the modal is closed
    setTimeout(() => {
      // Only bail for work that is genuinely still running. A publish is active
      // work happening server-side even though the user clicked the plain
      // "Close" (X) button rather than "Minimize" — wiping local/context state
      // here would strand the in-flight record.
      //
      // Deliberately NOT gated on isMinimized: nothing ever sets that back to
      // false when the work finishes, so treating "was minimized at some point"
      // as "still busy" meant the wizard never reset after a minimized publish,
      // and the finished audience was still sitting there the next time it
      // opened.
      if (isWorkInFlight) {
        return;
      }

      resetWizardState();

      // Refetch the table to show any newly created audiences (only if an audience was actually created)
      if (refetchHashedEmailLists && createdEmailListId) {
        try {
          refetchHashedEmailLists();
        } catch (error) {
          console.error("Error refetching email lists:", error);
        }
      }
    }, 0);
  };

  const getEmailModalColumns = () => [
    {
      title: getDataTypeLabel(),
      dataIndex: "email",
      key: "email",
      render: text => {
        let isValid;

        if (selectedDataType === "deviceIds") {
          isValid = DEVICE_ID_PATTERN.test(text);
        } else {
          // Check if it's a valid hash first (regardless of toggle state)
          const isValidHash = isValidHashedValue(text);

          // Check if it's in original (plain email) format
          const isOriginalFormat = emailRegex.test(text);

          // If toggle is ON, expect hashes; if OFF, expect original format
          isValid = emailsAlreadyHashed
            ? isValidHash  // When toggle is ON, prefer hashes but accept both
            : (isOriginalFormat || isValidHash); // When toggle is OFF, prefer original but accept hashes too
        }

        return (
          <span
            style={{
              color: isValid ? "inherit" : "red",
            }}
          >
            {text} {isValid ? "" : "(Invalid Value)"}
          </span>
        );
      },
    },
    {
      title: "Option",
      dataIndex: "option",
      key: "option",
      render: (_, email) => (
        <Popconfirm
          title={`Are you sure you want to delete this ${getDataTypeLabel().toLowerCase()}?`}
          onConfirm={async e => {
            const index = emails.findIndex(element => element === email.email);
            if (index !== -1) {
              const newData = [...emails];
              newData.splice(index, 1);
              setEmails(newData);
              message.success(`${getDataTypeLabel()} deleted`);
            }
          }}
          okText="Yes"
          cancelText="No"
          disabled={isUploadComplete}
        >
          <DeleteOutlined style={{ color: isUploadComplete ? '#d9d9d9' : 'inherit', cursor: isUploadComplete ? 'not-allowed' : 'pointer' }} />
        </Popconfirm>
      ),
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={4}>Upload Your {getDataTypeLabel()} List</Title>
            <Text style={{ display: "block", marginBottom: "24px" }}>
              Upload a CSV file containing your data. The system will process and validate them.
            </Text>

            {/* Data Type Selection */}
            <Row style={{ marginBottom: "24px" }}>
              <Col span={24}>
                <Text strong style={{ display: "block", marginBottom: "8px" }}>Select Data Type:</Text>
                <Radio.Group
                  value={selectedDataType}
                  onChange={(e) => {
                    setSelectedDataType(e.target.value);
                    // Loaded records and hash-detection state are only
                    // meaningful for the data type they were parsed under.
                    setEmails([]);
                    setInvalidRecords([]);
                    setEmailsAlreadyHashed(false);
                    setHemAutoDetected(false);
                    setDetectedInvalidFormat(null);
                  }}
                  disabled={isUploading || isUploadComplete}
                >
                  <Radio value="emails">Hashed Email</Radio>
                  <Radio value="deviceIds">Device ID</Radio>
                </Radio.Group>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: "24px" }}>
              <Col span={16}>
                <Form form={form} layout="vertical">
                  <Form.Item
                    label="Audience Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please input your audience name." },
                      {
                        pattern: /^[a-zA-Z0-9_-]+$/,
                        message: "Name can only contain letters, numbers, dash (-) and underscore (_).",
                      },
                      {
                        max: AUDIENCE_NAME_MAX_LENGTH,
                        message: `Audience name cannot exceed ${AUDIENCE_NAME_MAX_LENGTH} characters.`,
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter audience name"
                      disabled={isUploading || isUploadComplete}
                      maxLength={AUDIENCE_NAME_MAX_LENGTH}
                      showCount
                    />
                  </Form.Item>
                </Form>
              </Col>
              <Col span={8}>
                <Form.Item label=" ">
                  <TemplateCSVDownloader
                    tooltipMessage={`Download CSV template for ${getDataTypeLabel()} list`}
                    rows={activeCsvTemplate.rows}
                    templateName={activeCsvTemplate.templateName}
                    title={`Sample ${getDataTypeLabel()} list template`}
                    styles={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Popover
              content={
                <ol style={{ fontSize: "14px" }}>
                  <li>Enter the Audience List Name</li>
                  <li>Upload a CSV file. Remove headers from columns. Download the Sample Template for reference</li>
                  <li>If your CSV contains SHA-256 hashed emails, the system will auto-detect and enable pre-hashed mode</li>
                  <li>Click on the Next button to upload</li>
                  <li>Publishing to Nexxen starts automatically once the upload finishes</li>
                </ol>
              }
              title="Instructions"
            >
              <Button type="default" size="small" style={{ marginBottom: "16px" }}>
                Show Instructions
              </Button>
            </Popover>

            {selectedDataType !== "deviceIds" && (
              <Row gutter={16} style={{ marginBottom: "16px" }}>
                <Col span={12}>
                  <Text>Data is already hashed </Text>
                  <Tooltip title={
                    emails.length === 0
                      ? "Upload data first to enable this option"
                      : hemAutoDetected
                        ? "Auto-detected SHA-256 format"
                        : "Enable this option if the data is already hashed"
                  }>
                    <Switch
                      checked={emailsAlreadyHashed}
                      onChange={(checked) => {
                        setEmailsAlreadyHashed(checked);
                        if (!checked && hemAutoDetected) {
                          setHemAutoDetected(false);
                        }
                      }}
                      disabled={emails.length === 0 || isUploadComplete || hemAutoDetected}
                    />
                  </Tooltip>
                  {emailsAlreadyHashed && emails.length > 0 && (
                    <div style={{ marginTop: "4px" }}>
                      <Badge status="success" text={
                        <Text style={{ fontSize: "12px", color: "#52c41a" }}>
                          {hemAutoDetected ? "Auto-detected SHA-256 format" : "Auto-detected hashed format"}
                        </Text>
                      } />
                    </div>
                  )}
                </Col>
              </Row>
            )}

            {(isUploading || isUploadComplete) && (
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <Progress
                  percent={progress}
                  status={isUploading ? "active" : "success"}
                />
                {isUploadComplete && (
                  <div>
                    <Text
                      type="success"
                      style={{ color: "#52c41a", textAlign: "center", display: "block", marginTop: "8px" }}
                    >
                      Upload Complete. {`${savedHashedEmailCount}/${emails.length} ${getDataTypeLabel().toLowerCase()} were successfully saved.`}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", textAlign: "center", display: "block", marginTop: "4px" }}
                    >
                      Click "Next" to publish this audience to Nexxen automatically.
                    </Text>
                  </div>
                )}
              </div>
            )}

            <Spin spinning={isUploading || isParsing} tip={isParsing ? "Parsing CSV file..." : "Uploading..."}>
              {emails.length === 0 && invalidRecords.length === 0 ? (
                <Dragger
                    accept={".csv"}
                    showUploadList={false}
                    multiple={false}
                    customRequest={customRequest}
                    disabled={isParsing}
                    style={{ marginTop: "20px" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag CSV file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                      {isParsing ? "Processing your file..." : `Import your ${getDataTypeLabel()} here.`}
                    </p>
                  </Dragger>
                ) : (
                  <div>
                    <Row justify="space-between" align="middle" style={{ marginBottom: "10px" }}>
                      <Col>
                        <Space>
                          {emails.length > 0 && emails.length <= 1000 && (
                            <Upload
                              accept={".csv"}
                              showUploadList={false}
                              multiple={false}
                              customRequest={customRequest}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                disabled={isUploadComplete || isParsing}
                                loading={isParsing}
                              >
                                {isParsing ? "Parsing..." : "Add more"}
                              </Button>
                            </Upload>
                          )}
                          <Popconfirm
                            title={isUploading ? "Cancel upload and clear all records?" : "Clear all records?"}
                            description={isUploading ? "This will stop the current upload and remove all loaded records." : "This will remove all loaded records."}
                            onConfirm={async () => {
                              // Clear all records
                              setEmails([]);
                              setInvalidRecords([]);

                              // Reset hash-related states
                              setEmailsAlreadyHashed(false);
                              setHemAutoDetected(false);
                              setDetectedInvalidFormat(null);

                              // If upload is in progress, cancel it
                              if (isUploading) {
                                setIsUploading(false);
                                setProgress(0);
                                setHashedEmailCount(0);
                                setUploadComplete(false);

                                // Clear upload progress context
                                clearUpload();

                                // Refetch the table to remove "Uploading" status
                                if (refetchHashedEmailLists) {
                                  await refetchHashedEmailLists();
                                }

                                message.info("Upload cancelled and records cleared");
                              } else {
                                message.success("Records cleared successfully");
                              }
                            }}
                            okText="Yes, Clear"
                            cancelText="No"
                            okType="danger"
                            disabled={(emails.length === 0 && invalidRecords.length === 0) || isParsing}
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              disabled={(emails.length === 0 && invalidRecords.length === 0) || isParsing}
                              danger={isUploading}
                            >
                              {isUploading ? "Cancel & Clear" : "Clear all"}
                            </Button>
                          </Popconfirm>
                          {emails.length > 0 && emails.length <= 1000 && (
                            <Button
                              icon={sortIcon}
                              onClick={handleSortByName}
                              disabled={emails.length === 0 || isParsing}
                            >
                              Sort
                            </Button>
                          )}
                        </Space>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {invalidRecords.length > 0
                            ? `Valid: ${emails.length.toLocaleString()} | Invalid: ${invalidRecords.length.toLocaleString()}`
                            : `${getDataTypeLabel()} Count: ${emails.length.toLocaleString()}`
                          }
                        </Text>
                      </Col>
                    </Row>
                    {emails.length > 1000 ? (
                      <Alert
                        message={
                          invalidRecords.length > 0
                            ? `${emails.length.toLocaleString()} valid records loaded (${invalidRecords.length.toLocaleString()} invalid excluded)`
                            : `${emails.length.toLocaleString()} records loaded successfully`
                        }
                        description={
                          invalidRecords.length > 0
                            ? `Large dataset detected. Table preview is hidden to optimize performance. ${emails.length.toLocaleString()} valid records will be uploaded. ${invalidRecords.length.toLocaleString()} invalid records are shown below.`
                            : `Large dataset detected. Table preview is hidden to optimize performance. All ${emails.length.toLocaleString()} records will be uploaded when you click the Upload button.`
                        }
                        type={invalidRecords.length > 0 ? "warning" : "success"}
                        showIcon
                        style={{ marginTop: "12px" }}
                      />
                    ) : emails.length > 0 ? (
                      <PaginationTable
                        columns={getEmailModalColumns()}
                        data={emails.map(email => ({ email }))}
                        total={emails.length}
                        bordered={false}
                        size="small"
                        pageSize={10}
                        style={{ height: "300px", overflowY: "auto" }}
                      />
                    ) : null}

                    {/* Invalid Records Section */}
                    {invalidRecords.length > 0 && (
                      <div style={{ marginTop: "16px" }}>
                        <Alert
                          type="warning"
                          showIcon
                          message={
                            emails.length === 0
                              ? `All ${invalidRecords.length.toLocaleString()} records are invalid — no valid records to upload`
                              : `${invalidRecords.length.toLocaleString()} invalid records found`
                          }
                          description={
                            detectedInvalidFormat
                              ? `Data appears to be ${detectedInvalidFormat} which is not supported. Only plain emails/phones or hashed data (MD5, SHA-1, SHA-256, SHA-512) are accepted.${invalidRecords.length > 5000 ? " Download the CSV to review them." : ""}`
                              : invalidRecords.length > 5000
                                ? "Too many invalid records to display inline. Download the CSV to review them."
                                : "The following records are invalid and will be excluded from the upload."
                          }
                          style={{ marginBottom: "12px" }}
                          action={
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={downloadInvalidRecordsCsv}
                            >
                              Download CSV
                            </Button>
                          }
                        />
                        {invalidRecords.length <= 5000 && (
                          <Table
                            dataSource={invalidRecords.map((r, i) => ({ key: i, row: r.row, value: r.value }))}
                            columns={[
                              { title: "Row #", dataIndex: "row", key: "row", width: 80 },
                              { title: "Value", dataIndex: "value", key: "value", ellipsis: true },
                            ]}
                            size="small"
                            pagination={{ pageSize: 10, size: "small" }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
            </Spin>
          </div>
        );

      case 1: {
        // Nexxen is the only destination, so there is nothing to choose here
        // — the publish already started when Step 1 finished. The panel
        // itself is a presentational component: it just reflects whatever
        // this component learned from the last poll (or the activation call).
        return (
          <PublishStatusPanel
            audienceName={form.getFieldValue("name")}
            recordCount={savedHashedEmailCount}
            dataTypeLabel={getDataTypeLabel()}
            publishStatus={publishStatus}
            uploadErrorMessage={uploadErrorMessage}
            queryErrorMessage={queryErrorMessage}
            hasFailed={hasFailed}
            hasCompleted={isMappingComplete}
          />
        );
      }

      default:
        return null;
    }
  };

  // Step 2 has no "Next" button of its own — it's a read-only progress
  // panel — so these only ever drive the Step 1 upload button.
  const getNextButtonText = () => (isUploadComplete ? "Next" : "Upload");

  const getNextButtonIcon = () =>
    isUploadComplete ? <ArrowRightOutlined /> : <UploadOutlined />;

  const isNextDisabled = () =>
    emails.length === 0 || isUploading || isParsing;

  const isComplete = false; // Never show the complete state since workflow ends after step 1

  return (
    <Modal
      title="Add New Audience"
      open={visible}
      onCancel={handleClose}
      closable={!isUploading && !isParsing && !isPublishInFlight}
      maskClosable={!isUploading && !isParsing && !isPublishInFlight}
      keyboard={!isUploading && !isParsing && !isPublishInFlight}
      width="90%"
      style={{ top: 20 }}
      bodyStyle={{
        maxHeight: "80vh",
        overflowY: "hidden",
        padding: "0",
        position: "relative"
      }}
        footer={
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            {((isUploading && currentStep === 0) || isPublishInFlight) && (
              <Tooltip
                title={
                  currentStep === 1
                    ? "Minimize and continue publishing in the background"
                    : "Minimize and continue upload in background"
                }
              >
                <Button
                  key="minimize"
                  onClick={() => {
                    minimizeUpload();
                    onClose();
                  }}
                  icon={<MinusOutlined />}
                  size="large"
                  aria-label="Minimize"
                >
                  Minimize
                </Button>
              </Tooltip>
            )}
          </div>
          <Space size="large">
            {!isMappingComplete && (
              <Button
                key="close"
                onClick={handleClose}
                icon={<CloseOutlined />}
                disabled={isUploading || isParsing || isPublishInFlight}
                size="large"
              >
                Close
              </Button>
            )}

            {!isComplete && currentStep === 0 && (
              <Button
                key="next"
                type="primary"
                onClick={handleNext}
                disabled={isNextDisabled()}
                loading={isUploading}
                icon={getNextButtonIcon()}
                aria-label={getNextButtonText()}
                size="large"
              >
                {getNextButtonText()}
              </Button>
            )}

            {!isComplete && currentStep === 1 && isMappingComplete && (
              <Button
                key="finish"
                type="primary"
                onClick={handleFinishAndCheckLater}
                icon={<SaveOutlined />}
                size="large"
              >
                Finish and Check Later
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Row style={{ minHeight: "80vh" }}>
        {/* Left Sidebar - Steps Navigation */}
        <Col span={6} style={{ 
          backgroundColor: "#f5f5f5", 
          padding: "24px 16px",
          borderRight: "1px solid #e8e8e8"
        }}>
          <div style={{ marginBottom: "24px" }}>
            <Title level={4} style={{ margin: "0 0 8px 0", color: "#262626" }}>Progress</Title>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Step {Math.max(currentStep + 1, publishStatus?.stepProgress ?? 0)} of {steps.length}
            </Text>
          </div>

          <Steps
            current={-1}
            direction="vertical"
            size="small"
          >
            {steps
              .map((item, index) => {
                const stepNumber = index + 1;
                const backendStepProgress = publishStatus?.stepProgress ?? 0;
                // Step 1 is driven locally (it finishes before the backend
                // ever hears about the audience); Steps 2-4 are driven by
                // the poll, since Nexxen ingestion happens entirely
                // server-side.
                const isCompleted =
                  (index === 0 && isUploadComplete) ||
                  (index > 0 && stepNumber < backendStepProgress);
                const highlightIndex =
                  backendStepProgress > 0 ? backendStepProgress - 1 : currentStep;

                return (
                  <Step
                    key={item.title}
                    data-testid="workflow-step"
                    title={item.title}
                    description={item.description}
                    icon={
                      isCompleted ? (
                        <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "20px" }} />
                      ) : (
                        React.cloneElement(item.icon, {
                          style: {
                            color: index === highlightIndex ? "#1890ff" : "#bfbfbf",
                            fontSize: "20px"
                          }
                        })
                      )
                    }
                  />
                );
              })}
          </Steps>
        </Col>
        
        {/* Right Content Area */}
        <Col span={18} style={{ 
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ 
            flex: 1, 
            overflowY: "auto", 
            maxHeight: "calc(80vh - 80px)",
            padding: "24px"
          }}>
            {renderStepContent()}
          </div>
        </Col>
      </Row>

      {/* Custom parsing progress overlay - scoped to modal only */}
      {isParsing && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <Card
            style={{
              width: "400px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Progress
                type="circle"
                percent={parsingProgress}
                status="active"
                strokeColor={{
                  "0%": "#108ee9",
                  "100%": "#87d068",
                }}
              />
            </div>
            <Title level={4} style={{ marginBottom: "8px" }}>
              Parsing CSV File
            </Title>
            <Text type="secondary">
              Please wait while we process your file...
            </Text>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default GuidedWorkflowSteps;
